import {
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  Box,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useState } from "react";
import {
  StyledTextField,
} from "../../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import { useCreateTraitMutation, useGetClassroomsPaginatedQuery } from "./traitsApiSlice";
import { useGetSubSectionsPaginatedQuery } from "../sections/sectionsApiSlice";
import toast from "../../../../helpers/toast";

function CreateTraitForm() {
  const [subsectionSearch, setSubsectionSearch] = useState("");
  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: subsectionsData } = useGetSubSectionsPaginatedQuery({ page: 1, per_page: 10, q: subsectionSearch });
  const subsections = subsectionsData?.subsections ?? [];
  const { data: classroomsData } = useGetClassroomsPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];
  const [createTrait, { isLoading: createTraitLoading }] =
    useCreateTraitMutation();

  const Formik = useFormik({
    initialValues: {
      name: "",
      subsection_id: "",
      classroom_id: "",
      color: "#000000",
    },
    validationSchema: yup.object({
      name: yup.string().required("Required"),
    }).test(
      "subsection-or-classroom",
      "Either a subsection or a classroom is required",
      function (values) {
        if (!values.subsection_id && !values.classroom_id) {
          return this.createError({
            path: "subsection_id",
            message: "Either subsection or classroom is required",
          });
        }
        return true;
      }
    ),
    onSubmit: async (values) => {
      try {
        const payload = {
          name: values.name,
          color: values.color,
          ...(values.classroom_id
            ? { classroom_id: values.classroom_id }
            : { subsection_id: values.subsection_id }),
        };
        const response = await createTrait(payload).unwrap();
        if (response.id) {
          toast.success("Trait created successfully");
          Formik.resetForm();
        } else {
          toast.error("Trait creation failed");
        }
      } catch (error) {
        toast.error(error?.data?.message || error?.message || "Something went wrong");
      }
    },
  });
  return (
    <>
      <Box className="paperHeader">
        <Typography variant="formHeading">Create Trait</Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={Formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Trait</Typography>
                <StyledTextField
                  autoFocus
                  variant="outlined"
                  id="name"
                  name="name"
                  value={Formik.values.name}
                  onChange={Formik.handleChange}
                  error={Formik.touched.name && Boolean(Formik.errors.name)}
                />
                <FormHelperText variant="error">
                  {Formik.touched.name && Formik.errors.name}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs="auto">
              <FormControl>
                <Typography variant="formSubHeading">Color</Typography>
                <input
                  type="color"
                  id="color"
                  name="color"
                  value={Formik.values.color}
                  onChange={Formik.handleChange}
                  style={{ width: "48px", height: "40px", cursor: "pointer", border: "none", padding: 0, background: "none" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Subsection</Typography>
                <Autocomplete
                  disabled={Boolean(Formik.values.classroom_id)}
                  options={subsections}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={subsections.find(s => s.id === Formik.values.subsection_id) || null}
                  onChange={(_, value) => {
                    Formik.setFieldValue("subsection_id", value?.id || "");
                    if (value) Formik.setFieldValue("classroom_id", "");
                  }}
                  onInputChange={(_, value, reason) => { if (reason === "input") setSubsectionSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder={Formik.values.classroom_id ? "Disabled — classroom selected" : "Search subsection..."}
                      error={Formik.touched.subsection_id && Boolean(Formik.errors.subsection_id)}
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {Formik.touched.subsection_id && Formik.errors.subsection_id}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">
                  Classroom{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    (optional — overrides subsection traits for this class)
                  </Typography>
                </Typography>
                <Autocomplete
                  disabled={Boolean(Formik.values.subsection_id)}
                  options={classrooms}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={classrooms.find(c => c.id === Formik.values.classroom_id) || null}
                  onChange={(_, value) => {
                    Formik.setFieldValue("classroom_id", value?.id || "");
                    if (value) Formik.setFieldValue("subsection_id", "");
                  }}
                  onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder={Formik.values.subsection_id ? "Disabled — subsection selected" : "Search classroom..."}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} textAlign={"right"}>
              <StyledLoadingButton loading={createTraitLoading} type="submit">
                Submit
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
}

export default CreateTraitForm;
